import {
  Box,
  Button,
  Container,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Stack,
  Text,
  List,
  ListItem,
  useToast,
} from '@chakra-ui/react';

import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { useState } from 'react';

import CountrySelect from 'components/CountrySelect';
import PhoneExtensionSelect from 'components/PhoneExtensionSelect';

import { HiEye, HiEyeOff } from 'react-icons/hi';
import { FaGraduationCap, FaChalkboardTeacher } from 'react-icons/fa';
import { UserAPI } from '../../API/user';
import Footer from '../footer/Footer';
import Navbar from '../navbar';
import axios from 'axios';
import {
  validatePassword,
  checkPasswordRules,
} from '../../utils/password-validation';
import { getValidationInputProps } from 'utils/validations';
import TosModal from '../tos-modal';
import { useForm } from 'react-hook-form';
import { ROUTES } from 'constants/routes';
import { trackMetaEvent } from '../../hooks/useMetaPixel';

type Role = 'STUDENT' | 'TUTOR';

enum STATES {
  first,
  second,
}

interface SignupFormData {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  confirmPassword: string;
  address_1: string;
  address_2: string;
  phone: string;
  country: string;
  postal_code: string;
}

export default function Signup({ role: roleProp }: { role?: Role }) {
  const [state, setState] = useState<STATES>(STATES.first);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPasswordRequirements, setShowPasswordRequirements] =
    useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [phoneExtension, setPhoneExtension] = useState('');
  const [role] = useState<Role>(roleProp ?? 'STUDENT');
  // Role is fixed by the route (/auth/signup/student | /auth/signup/tutor).
  // When undefined (the /auth/signup landing) we render the chooser instead.
  const isChooser = !roleProp;
  const router = useRouter();


  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  const [showTos, setShowTos] = useState(false);
  const toast = useToast({ position: 'top' });

  const { register, handleSubmit, watch, setValue, getValues } =
    useForm<SignupFormData>({
      defaultValues: {
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        confirmPassword: '',
        address_1: '',
        address_2: '',
        phone: '',
        country: '',
        postal_code: '',
      },
    });

  const watchedPassword = watch('password');
  const watchedConfirmPassword = watch('confirmPassword');
  const watchedCountry = watch('country');

  const isPasswordValid = validatePassword(watchedPassword).isValid;
  const doPasswordsMatch =
    watchedConfirmPassword.length > 0 &&
    watchedPassword === watchedConfirmPassword;

  const formSubmitHandler = async (data: SignupFormData) => {
    setError('');

    const passwordValidation = validatePassword(data.password);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.message || 'Invalid password');
      return;
    }

    if (data.confirmPassword !== data.password) {
      setError('Passwords do not match.');
      return;
    }

    if (!data?.email?.match(/.+\@.+\..+/)) {
      setError('Invalid email');
      return;
    }

    if (!data.first_name?.trim()) {
      setError('First name is required');
      return;
    }

    if (!data.last_name?.trim()) {
      setError('Last name is required');
      return;
    }

    switch (state) {
      case STATES.first:
        setState(STATES.second);
        return;
    }

    if (!data.address_1?.trim()) {
      setError('Address is required');
      return;
    }

    if (!data.country) {
      setError('Country is required');
      return;
    }

    const isUK = data.country === 'GB';

    if (isUK && !data?.postal_code) {
      setError('Postcode is required for UK residents');
      return;
    }

    if (isUK && data?.postal_code) {
      try {
        await axios.get(`https://postcodes.io/postcodes/${data?.postal_code}`);
      } catch {
        setError('Please enter a valid UK postal code');
        return;
      }
    }

    if (!data.phone) {
      setError('Phone number is required');
      return;
    }
    if (!phoneExtension) {
      setError('Please select a phone extension');
      return;
    }

    setShowTos(true);
  };

  const handleTosAccept = () => {
    setShowTos(false);
    setIsLoading(true);

    const loadingToastId = toast({
      status: 'info',
      title: 'Creating your account...',
      duration: null,
      isClosable: false,
    });

    const data = getValues();

    UserAPI.register({
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      password: data.password,
      address_1: data.address_1,
      address_2: data.address_2,
      phone: data.phone
        ? role === 'TUTOR'
          ? `${phoneExtension}${data.phone.replace(/^0/, '')}`
          : `${phoneExtension} ${data.phone}`.trim()
        : '',
      country: data.country,
      postal_code: data.postal_code,
      role,
    })
      .then(() => {
        trackMetaEvent('CompleteRegistration', {
          content_name: role === 'TUTOR' ? 'Tutor Signup' : 'Student Signup',
          status: role.toLowerCase(),
        });
        const actionType = role === 'TUTOR' ? 'tutor-signup' : 'student-signup';
        const next = role === 'TUTOR' ? '/tutor/qualifications' : '/search';
        const verifyUrl = `/verify-email?email=${encodeURIComponent(
          data.email
        )}&next=${encodeURIComponent(next)}`;
        const convId = 'reg_' + (role === 'TUTOR' ? 'tut_' : 'stu_') + Date.now();
        try {
          sessionStorage.setItem(
            `ve_pending_conv_${actionType}`,
            JSON.stringify({ cid: convId })
          );
        } catch {}
        router.push(
          `/thank-you?action=${actionType}&next=${encodeURIComponent(
            verifyUrl
          )}&cid=${convId}`
        );
      })
      .catch((er) => {
        console.warn(er);
        if (loadingToastId) toast.close(loadingToastId);
        if (er.response?.status >= 500) {
          setError('Something went wrong! Please try again');
          return;
        }
        const msg = er.response?.data.message;
        setError(Array.isArray(msg) ? msg.join('. ') : msg || er.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleTosDecline = () => {
    setShowTos(false);
  };

  const renderPasswordRequirements = () => {
    if (!showPasswordRequirements) return null;

    return (
      <FormHelperText>
        <List spacing={1} fontSize="sm">
          {checkPasswordRules(watchedPassword).map(({ label, passed }) => (
            <ListItem key={label} display="flex" alignItems="center" gap={2}>
              <Text color={passed ? 'green.500' : 'gray.500'}>
                {passed ? '✓' : '○'}
              </Text>
              {label}
            </ListItem>
          ))}
        </List>
      </FormHelperText>
    );
  };

  const phoneHelperText =
    role === 'TUTOR'
      ? 'Select a phone extension or it will auto-set from your selected country. Provide accurate Phone Number because student will contact you on this number.'
      : 'Select a phone extension or it will auto-set from your selected country.';

  return (
    <Flex flexDir="column" minH="100vh">
      <Navbar />
      <Flex
        justifyContent={'center'}
        alignItems="center"
        marginY="4em !important"
        flex="1"
        w="100%"
        px="1em"
      >
        <Container
          boxShadow={'md'}
          w="100%"
          maxW="50em"
          borderRadius="8px"
          p="1em 1.5em"
          border="1px solid #3333"
        >
          {isChooser ? (
            <Stack spacing="1em" py="0.5em">
              <Heading
                size="md"
                textAlign="center"
                color="accent.400"
                fontWeight="600"
              >
                Create your account
              </Heading>
              <Text textAlign="center" color="gray.500" fontSize="0.9em">
                How will you use Vital Educators? Pick one to continue.
              </Text>
              <Stack
                direction={{ base: 'column', md: 'row' }}
                spacing="1em"
                pt="0.5em"
              >
                {[
                  {
                    r: 'STUDENT' as Role,
                    icon: FaGraduationCap,
                    title: 'I want to find a tutor',
                    desc: 'Book lessons and message tutors directly.',
                  },
                  {
                    r: 'TUTOR' as Role,
                    icon: FaChalkboardTeacher,
                    title: 'I want to teach & earn',
                    desc: 'Create a profile and get matched with students.',
                  },
                ].map(({ r, icon: Icon, title, desc }) => (
                  <Box
                    as="button"
                    type="button"
                    key={r}
                    flex="1"
                    textAlign="left"
                    p="1.25em"
                    borderRadius="10px"
                    border="1px solid #e2e8f0"
                    transition="all 0.15s"
                    _hover={{
                      borderColor: 'accent.400',
                      bg: 'accent.50',
                      transform: 'translateY(-2px)',
                      boxShadow: 'sm',
                    }}
                    onClick={() =>
                      router.push(r === 'STUDENT' ? ROUTES.studentSignup : ROUTES.tutorSignup)
                    }
                  >
                    <Box as={Icon} size="28px" color="accent.400" mb="0.5em" />
                    <Text fontWeight="600" color="gray.800" mb="0.25em">
                      {title}
                    </Text>
                    <Text fontSize="0.82em" color="gray.500" lineHeight="1.4">
                      {desc}
                    </Text>
                  </Box>
                ))}
              </Stack>
              <Text textAlign="center" fontSize="0.85em" pt="0.5em">
                {'Already have an account? '}
                <NextLink href={ROUTES.login} passHref={true}>
                  <Link color="blue.500">Log in</Link>
                </NextLink>
              </Text>
            </Stack>
          ) : (
          <form onSubmit={handleSubmit(formSubmitHandler)}>
            <Stack direction={'column'} spacing={'1em'}>
              {/* ── Chosen account type + change link ─────────── */}
              <Flex
                align="center"
                justify="center"
                gap="0.4em"
                fontSize="0.85em"
                color="gray.600"
              >
                <Text>
                  Creating a{' '}
                  <Text as="span" fontWeight="700" color="accent.400">
                    {role === 'STUDENT' ? 'Student' : 'Tutor'}
                  </Text>{' '}
                  account
                </Text>
                <Box
                  as="button"
                  type="button"
                  color="accent.400"
                  textDecoration="underline"
                  onClick={() => router.push(ROUTES.signup)}
                >
                  Change
                </Box>
              </Flex>

              <Flex align="center" position="relative" mb="1em">
                {state === STATES.second && (
                  <Box
                    as="button"
                    type="button"
                    position="absolute"
                    left="0"
                    top="50%"
                    transform="translateY(-50%)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    w="34px"
                    h="34px"
                    borderRadius="8px"
                    border="0.5px solid #ddd"
                    bg="#fafafa"
                    color="#555"
                    fontSize="18px"
                    cursor="pointer"
                    _hover={{
                      color: '#2d2a8e',
                      borderColor: '#2d2a8e',
                      bg: '#f0effc',
                    }}
                    onClick={() => setState(STATES.first)}
                  >
                    ←
                  </Box>
                )}
                <Heading
                  size="md"
                  w="100%"
                  textAlign={'center'}
                  borderRadius={'2px'}
                  p="0.5em 1em"
                  bg="accent.50"
                  color="accent.400"
                  fontWeight="500"
                >
                  {role === 'STUDENT' ? 'Student' : 'Tutor'} Signup
                </Heading>
              </Flex>
              {error &&
                error
                  .split('. ')
                  .filter(Boolean)
                  .map((line, i) => (
                    <Text key={i} color="#e00">
                      {line}
                    </Text>
                  ))}
              {
                {
                  [STATES.second]: (
                    <Box key="page-2">
                      <Stack w="100%" direction={['column']}>
                        <FormControl isRequired>
                          <FormLabel htmlFor="address">Address 1</FormLabel>
                          <Input
                            id="address"
                            type="address"
                            placeholder="Your current address"
                            {...register('address_1')}
                          ></Input>
                        </FormControl>

                        <FormControl>
                          <FormLabel htmlFor="address2">Address 2</FormLabel>
                          <Input
                            id="address2"
                            type="address"
                            placeholder="Your second address"
                            {...register('address_2')}
                          ></Input>
                        </FormControl>
                        <CountrySelect
                          value={watchedCountry}
                          onSelect={(value) => {
                            setValue('country', value);
                          }}
                        />
                      </Stack>
                      <Stack w="100%" direction={['column', null, 'row']}>
                        <FormControl isRequired={watchedCountry === 'GB'}>
                          <FormLabel htmlFor="postcode">Postcode</FormLabel>
                          <Input
                            id="postcode"
                            {...register('postal_code')}
                            placeholder="Enter postcode here"
                            isDisabled={watchedCountry !== 'GB'}
                          ></Input>
                          <FormHelperText>
                            {watchedCountry === 'GB'
                              ? 'Required for UK residents.'
                              : 'Only applicable for UK residents.'}
                          </FormHelperText>
                        </FormControl>
                        <FormControl isRequired>
                          <FormLabel htmlFor="phone">Your Phone</FormLabel>
                          <Stack direction="row" spacing={2} align="flex-end">
                            <PhoneExtensionSelect
                              value={phoneExtension}
                              onChange={setPhoneExtension}
                              countryCode={watchedCountry}
                              w="140px"
                              flexShrink={0}
                            />
                            <Input
                              id="phone"
                              type="tel"
                              placeholder="Phone number"
                              {...register('phone', {
                                onChange: (e) =>
                                  setValue(
                                    'phone',
                                    e.target.value.replace(/[^0-9]/g, ''),
                                    {
                                      shouldDirty: true,
                                    }
                                  ),
                              })}
                            />
                          </Stack>
                          <FormHelperText>{phoneHelperText}</FormHelperText>
                        </FormControl>
                      </Stack>
                    </Box>
                  ),
                  [STATES.first]: (
                    <Box key="page-1">
                      <Stack direction={['column', null, 'row']} w="100%">
                        <FormControl isRequired>
                          <FormLabel htmlFor="first_name">First name</FormLabel>
                          <Input
                            id="first_name"
                            type="first_name"
                            placeholder="Your first name"
                            {...register('first_name')}
                          ></Input>
                        </FormControl>

                        <FormControl isRequired>
                          <FormLabel htmlFor="last_name">Last Name</FormLabel>
                          <Input
                            id="last_name"
                            type="last_name"
                            placeholder="Your last name"
                            {...register('last_name')}
                          ></Input>
                        </FormControl>
                      </Stack>
                      <Stack direction={['column', null, 'row']} w="100%">
                        <FormControl isRequired>
                          <FormLabel>Email</FormLabel>
                          <Input
                            placeholder="Enter your email"
                            {...register('email')}
                          />
                        </FormControl>
                      </Stack>

                      <Stack direction={['column', null, 'row']} w="100%">
                        <FormControl
                          isRequired
                          isInvalid={passwordTouched && !isPasswordValid}
                        >
                          <FormLabel>Password</FormLabel>
                          <InputGroup>
                            <Input
                              placeholder="Enter your password"
                              type={showPassword ? 'text' : 'password'}
                              {...register('password')}
                              onFocus={() => {
                                setShowPasswordRequirements(true);
                                setPasswordTouched(true);
                              }}
                              onBlur={() => {
                                setShowPasswordRequirements(false);
                                setPasswordTouched(true);
                              }}
                              {...(passwordTouched
                                ? getValidationInputProps(isPasswordValid)
                                : {})}
                            />
                            <InputRightElement>
                              <IconButton
                                aria-label={
                                  showPassword
                                    ? 'Hide password'
                                    : 'Show password'
                                }
                                icon={showPassword ? <HiEyeOff /> : <HiEye />}
                                variant="ghost"
                                size="sm"
                                onClick={() => setShowPassword((v) => !v)}
                                tabIndex={-1}
                              />
                            </InputRightElement>
                          </InputGroup>
                          {renderPasswordRequirements()}
                          {passwordTouched && !isPasswordValid && (
                            <FormHelperText color="red.500">
                              Please enter a valid password
                            </FormHelperText>
                          )}
                        </FormControl>
                        <FormControl
                          isRequired
                          isInvalid={
                            confirmPasswordTouched && !doPasswordsMatch
                          }
                        >
                          <FormLabel>Confirm Password</FormLabel>
                          <InputGroup>
                            <Input
                              placeholder="Enter your password again"
                              type={showConfirmPassword ? 'text' : 'password'}
                              {...register('confirmPassword')}
                              onChange={(e) => {
                                setValue('confirmPassword', e.target.value);
                                setConfirmPasswordTouched(true);
                              }}
                              onFocus={() => setConfirmPasswordTouched(true)}
                              onBlur={() => setConfirmPasswordTouched(true)}
                              {...(confirmPasswordTouched
                                ? getValidationInputProps(doPasswordsMatch)
                                : {})}
                            />
                            <InputRightElement>
                              <IconButton
                                aria-label={
                                  showConfirmPassword
                                    ? 'Hide confirm password'
                                    : 'Show confirm password'
                                }
                                icon={
                                  showConfirmPassword ? <HiEyeOff /> : <HiEye />
                                }
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  setShowConfirmPassword((v) => !v)
                                }
                                tabIndex={-1}
                              />
                            </InputRightElement>
                          </InputGroup>
                          {confirmPasswordTouched && !doPasswordsMatch && (
                            <FormHelperText color="red.500">
                              Passwords do not match
                            </FormHelperText>
                          )}
                        </FormControl>
                      </Stack>
                    </Box>
                  ),
                }[state]
              }

              <Button
                colorScheme={'accent'}
                type="submit"
                isLoading={isLoading}
                maxW="max-content"
                px="2em"
                alignSelf={'center'}
              >
                {state == STATES.second ? 'Signup' : 'Next'}
              </Button>
              <hr />
              <Text textAlign={'center'}>
                {'Already have an Account? '}
                <NextLink
                  href={
                    role === 'TUTOR' ? ROUTES.tutorLogin : ROUTES.studentLogin
                  }
                  passHref={true}
                >
                  <Link href="#" color="blue.500">
                    Login
                  </Link>
                </NextLink>
              </Text>
            </Stack>
          </form>
          )}
        </Container>
      </Flex>
      <Footer />
      <TosModal
        isOpen={showTos}
        onAccept={handleTosAccept}
        onDecline={handleTosDecline}
      />
    </Flex>
  );
}
